import { useState } from 'react';

const useShortcutNavigation = ({
    getNextShorcut,
    allRaccourci,
    dataNextShortcut,
    setDataNextShortcut,
    nextShortcutString,
    setNextShortcutString,
    lastShortCutClicked,
    setLastShortCutClicked,
    setDisabled,
    onArticleClick = null,
    onGiftcardClick = null,
}) => {
    const [colorStack, setColorStack] = useState([]);
    const [fieldsStack, setFieldsStack] = useState([]);
    // Pile des uid parents pour la navigation fullCustom (frontend seulement)
    const [fullCustomParentUids, setFullCustomParentUids] = useState([]);

    const resetSearchRaccourci = () => {
        setDataNextShortcut([]);
        setNextShortcutString('');
        setLastShortCutClicked([]);
        setColorStack([]);
        setFieldsStack([]);
        setFullCustomParentUids([]);
        setDisabled(false);
    };

    const raccourciAction = (name, articleId, categorieId) => {
        if (name === 'giftcard') {
            if (onGiftcardClick) onGiftcardClick();
            return;
        }
        if (name === 'article') {
            if (onArticleClick) onArticleClick(articleId);
            return;
        }

        // Navigation dans un sous-groupe fullCustom — purement frontend, pas d'appel API
        // (le backend renvoie toujours la totalité de construction au premier clic)
        if (name === 'fullCustom' && dataNextShortcut.length > 0) {
            const inheritedColor = colorStack[0] || false;
            setColorStack([inheritedColor, ...colorStack]);
            setFullCustomParentUids([categorieId, ...fullCustomParentUids]);
            setLastShortCutClicked([name, ...lastShortCutClicked]);
            setFieldsStack([null, ...fieldsStack]);
            return;
        }

        const sourceList = dataNextShortcut.length > 0 ? dataNextShortcut : allRaccourci;
        let clickedItem;
        if (name === 'fromCustomField' && dataNextShortcut.length > 0) {
            // categorieId est l'index dans le tableau — évite les faux matches quand plusieurs items partagent le même id
            clickedItem = sourceList[categorieId];
        } else if (name === 'fromCustomField' || name === 'fullCustom' || dataNextShortcut.length > 0) {
            // fullCustom n'a pas de champ *_id : on recherche par id direct
            clickedItem = sourceList.find(item => item.id === categorieId);
        } else {
            clickedItem = sourceList.find(item => item[name + '_id'] === categorieId);
        }

        const inheritedColor = clickedItem?.color || colorStack[0] || false;
        setColorStack([inheritedColor, ...colorStack]);

        if (name === 'fromCustomField') {
            const actualId = clickedItem?.id ?? categorieId;
            const paramCategorieId = nextShortcutString !== ''
                ? actualId.toString() + '-' + nextShortcutString
                : actualId.toString();
            const cfNames = (clickedItem?.customfield_name || '').split('/!')
            const cfValues = (clickedItem?.customfield_value || '').split('/!')
            const fields = cfNames.map((n, i) => ({ name: n, value: cfValues[i] ?? '' }))
            getNextShorcut(name, actualId.toString(), fields);
            setFieldsStack([fields, ...fieldsStack]);
            setNextShortcutString(paramCategorieId);
        } else if (name === 'fullCustom') {
            // Premier clic sur une tuile fullCustom — charge l'ensemble de construction depuis le backend
            const actualId = clickedItem?.id ?? categorieId;
            // Recherche par niveau:1 car l'ordre du tableau peut varier après éditions
            const rootEntry = clickedItem?.construction?.find(c => c.niveau === 1)
            const rootUid = rootEntry?.uid;
            setFullCustomParentUids([rootUid]);
            getNextShorcut(name, actualId.toString(), null, { niveau: 1, uid: rootUid });
            setFieldsStack([null, ...fieldsStack]);
            setNextShortcutString(actualId.toString());
        } else {
            const paramCategorieId = nextShortcutString !== ''
                ? categorieId.toString() + '-' + nextShortcutString
                : categorieId.toString();
            getNextShorcut(name, paramCategorieId);
            setFieldsStack([null, ...fieldsStack]);
            setNextShortcutString(paramCategorieId);
        }
        setLastShortCutClicked([name, ...lastShortCutClicked]);
        setDisabled(true);
    };

    const getPreviousSearchRaccourci = () => {
        // Retour dans une navigation fullCustom imbriquée (pas d'appel API)
        if (fullCustomParentUids.length > 1) {
            setFullCustomParentUids(fullCustomParentUids.slice(1));
            setLastShortCutClicked(lastShortCutClicked.filter((_, i) => i !== 0));
            setFieldsStack(fieldsStack.filter((_, i) => i !== 0));
            setColorStack(colorStack.slice(1));
            return;
        }

        const arrRaccourci = nextShortcutString.split('-');
        if (arrRaccourci.length === 1) {
            resetSearchRaccourci();
        } else {
            const prevType = lastShortCutClicked[1];
            const stRaccourciIds = arrRaccourci.slice(1).join('-');
            if (prevType === 'fromCustomField') {
                const prevFields = fieldsStack[1] ?? null;
                getNextShorcut(prevType, stRaccourciIds, prevFields);
            } else {
                getNextShorcut(prevType, stRaccourciIds);
            }
            setLastShortCutClicked(lastShortCutClicked.filter((_, i) => i !== 0));
            setFieldsStack(fieldsStack.filter((_, i) => i !== 0));
            setNextShortcutString(stRaccourciIds);
            setColorStack(colorStack.slice(1));
            setDisabled(true);
        }
    };

    return {
        colorStack,
        raccourciAction,
        getPreviousSearchRaccourci,
        resetSearchRaccourci,
        // uid du parent courant pour filtrer les entrées fullCustom à afficher
        fullCustomCurrentParentUid: fullCustomParentUids[0] ?? null,
    };
};

export default useShortcutNavigation;
